import { useQueryClient } from "@tanstack/react-query";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import Head from "expo-router/head";
import React, { useEffect, useRef } from "react";
import { Image, ScrollView, StyleSheet, View } from "react-native";
import { z } from "zod";

import {
  FormError,
  FormProvider,
  FormTextInput,
  SubmitButton,
  useFormWrapper,
} from "@luna-restaurant/forms";
import { useTranslation } from "@luna-restaurant/i18n";
import { ThemedText, ThemedToast, useCustomTheme } from "@luna-restaurant/ui";

import { ThemedFooter } from "@/components/ThemedFooter";
import { useAuthApi } from "@/services/authApi";

const LunaWelcomeImage = require("@/assets/images/luna-welcome-image.png");

const IndexScreen = () => {
  const router = useRouter();
  const { sessionExpired } = useLocalSearchParams<{
    sessionExpired?: string | string[];
  }>();
  const { t } = useTranslation();
  const { colors, fonts } = useCustomTheme();
  const queryClient = useQueryClient();
  const sessionToastShownRef = useRef(false);

  const sessionExpiredParam = Array.isArray(sessionExpired)
    ? sessionExpired[0]
    : sessionExpired;

  useEffect(() => {
    if (sessionExpiredParam !== "1") {
      sessionToastShownRef.current = false;
      return;
    }

    if (sessionToastShownRef.current) {
      return;
    }

    sessionToastShownRef.current = true;
    ThemedToast.show({
      type: "error",
      text1: t("login.sessionExpiredTitle"),
      text2: t("login.sessionExpiredMessage"),
      autoHide: false,
      visibilityTime: 0,
      props: {
        dismissible: true,
        closeButtonLabel: t("common.close"),
      },
    });

    router.replace("/de");
  }, [sessionExpiredParam, t, router]);

  const LoginSchema = z.object({
    email: z.email(t("login.emailIsRequired")),
    password: z.string().min(1, t("login.passwordIsRequired")),
  });

  const form = useFormWrapper<typeof LoginSchema>({
    schema: LoginSchema,
    defaultValues: { email: "", password: "" },
    mode: "onSubmit",
  });

  const { login } = useAuthApi();

  const onSubmit: Parameters<typeof form.submit>[0] = async ({ values }) => {
    try {
      await login({ email: values.email, password: values.password });
      queryClient.removeQueries({ queryKey: ["currentUser"] });
      queryClient.removeQueries({ queryKey: ["currentSchool"] });
      router.navigate("/de/(auth)/speiseplan");
    } catch (e) {
      console.error(e);
      form.setError("root", { message: t("login.loginFailed") });
    }
  };

  return (
    <>
      <Head>
        <title>{t("titles.login")}</title>
      </Head>
      <ScrollView
        contentContainerStyle={[
          styles.scrollView,
          { backgroundColor: colors.successLighter },
        ]}
      >
        <View style={styles.cardContainer}>
          <View style={[styles.card, { backgroundColor: colors.background }]}>
            <View style={styles.imageSection}>
              <Image source={LunaWelcomeImage} style={styles.image} />
              <LinearGradient
                colors={["rgba(4,67,19,0)", "rgba(4,67,19,1)"]}
                style={styles.imageOverlay}
              >
                <View>
                  <ThemedText
                    style={[
                      styles.welcomeText,
                      { ...fonts.headline, color: colors.background },
                    ]}
                  >
                    {t("login.imageHeadline")}
                  </ThemedText>
                </View>
                <ThemedText
                  style={[
                    styles.subtitleText,
                    { ...fonts.bold, color: colors.background },
                  ]}
                >
                  {t("login.imageSubline")}
                </ThemedText>
              </LinearGradient>
            </View>
            <View style={styles.formSection}>
              <View style={styles.loginTitleContainer}>
                <ThemedText
                  style={[
                    styles.loginTitle,
                    { ...fonts.semiBold, color: colors.text },
                  ]}
                >
                  {t("login.formHeadline")}
                </ThemedText>
                <ThemedText
                  style={[
                    styles.descriptionText,
                    { ...fonts.regular, color: colors.text },
                  ]}
                >
                  {t("login.formSubline")}
                </ThemedText>
              </View>
              <FormProvider form={form}>
                <View style={styles.inputContainer}>
                  <FormError />
                  <FormTextInput
                    name="email"
                    label={t("login.emailPlaceholder")}
                    icon={{
                      iconSet: "material-community",
                      icon: "email-outline",
                    }}
                  />

                  <FormTextInput
                    name="password"
                    label={t("login.passwordPlaceholder")}
                    icon={{
                      iconSet: "material-community",
                      icon: "lock-outline",
                    }}
                    type="password"
                  />

                  <SubmitButton
                    title={t("login.loginButton")}
                    onPress={() => form.submit(onSubmit)}
                    buttonStyle={[
                      styles.loginButton,
                      { backgroundColor: colors.border },
                    ]}
                    buttonTextStyle={[
                      styles.loginButtonText,
                      { color: colors.background, ...fonts.bold },
                    ]}
                    iconName="arrow-right"
                    iconPosition="right"
                    iconColor={colors.background}
                  />
                </View>
              </FormProvider>
            </View>
          </View>
        </View>
        <ThemedFooter />
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    minHeight: "100%",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  card: {
    width: "70%",
    maxWidth: 1100,
    maxHeight: 624,
    padding: 40,
    gap: 40,
    borderRadius: 28,
    boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.1)",
    elevation: 8,
    flexDirection: "row",
    marginVertical: 40,
  },
  imageSection: {
    flex: 1,
    position: "relative",
  },
  image: {
    width: "100%",
    height: "100%",
    borderRadius: 16,
  },
  imageOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: 40,
    borderRadius: 16,
  },
  welcomeText: {
    fontSize: 64,
    lineHeight: 70.4,
  },
  subtitleText: {
    fontSize: 24,
    lineHeight: 30,
    marginTop: 24,
  },
  formSection: {
    flex: 1,
    justifyContent: "center",
    padding: 30,
    gap: 32,
  },
  loginTitleContainer: {
    gap: 32,
  },
  loginTitle: {
    fontSize: 32,
    lineHeight: 40,
  },
  descriptionText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
  inputContainer: {
    gap: 8,
  },
  loginButton: {
    borderRadius: 70,
    height: 56,
    marginTop: 24,
  },
  loginButtonText: {
    fontSize: 16,
    lineHeight: 20.8,
  },
});

export default IndexScreen;
